import { useEffect, useRef } from "react"

/**
 * Hook that closes a dropdown when clicking outside the referenced element.
 * Uses a click-count debounce to skip the opening click that bubbles up.
 * See docs/gotchas/click-outside-immediate-close.md
 *
 * @param {React.RefObject} ref - Ref to the container element
 * @param {boolean} isOpen - Whether the dropdown is currently open
 * @param {Function} onClose - Callback to close the dropdown
 */
export function useClickOutside(ref, isOpen, onClose) {
  const clickCountRef = useRef(0)

  // Reset click counter when dropdown opens/closes
  useEffect(() => {
    clickCountRef.current = 0
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (e) => {
      // Skip the first click (the one that opened us)
      if (clickCountRef.current < 1) {
        clickCountRef.current = 1
        return
      }

      if (ref.current && !ref.current.contains(e.target)) {
        onClose()
      }
    }

    document.addEventListener("click", handleClickOutside)
    return () => document.removeEventListener("click", handleClickOutside)
  }, [isOpen, ref, onClose])
}
